import React from 'react';
import ReactDOM from 'react-dom/client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { App } from './App';
import { queryClient } from './lib/query-client';
import { persistOptions } from './lib/query-persist';
import './index.css';

// PersistQueryClientProvider rather than a plain QueryClientProvider: it holds
// the first render until the persisted cache has been restored, so queries
// mount with data already present instead of firing a request and being
// overwritten a tick later. Restoration is a synchronous localStorage read, so
// the delay is sub-millisecond — and it is what turns a reload from "every
// query refetches" into "the screen paints, then revalidates in background".
//
// onSuccess invalidates everything the moment restore finishes. Without it, a
// reload inside a query's staleTime window paints the restored (possibly
// hours-old, given another user's change since) data and makes NO network
// request at all — maxAge and staleTime are independent gates, so "I hit
// refresh" would otherwise not guarantee freshness. This costs nothing extra:
// invalidateQueries only refetches queries that are actively mounted, i.e.
// exactly the ones the very next render puts on screen.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={() => queryClient.invalidateQueries()}
    >
      <BrowserRouter>
        <TooltipProvider>
          <App />
          <Toaster richColors position="top-right" />
        </TooltipProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
